// Patient-portal date parser — drop-in replacement for `new Date(serverField)`.
//
// Backend convention: every Instant on a patient-portal DTO carries
// {@code @JsonFormat(STRING, "yyyy-MM-dd HH:mm:ss", timezone="UTC")}, so the
// wire shape is e.g. "2026-05-05 14:30:00" — UTC implied, no Z suffix.
//
// `new Date("2026-05-05 14:30:00")` is browser-dependent:
//   - Chrome / Firefox: parsed as LOCAL time (off by the user's timezone)
//   - Safari: returns Invalid Date
//
// This helper:
//   - null / undefined / "" → null
//   - Date instance → returned as-is
//   - number → new Date(number)  (assumes ms-since-epoch)
//   - string matching "yyyy-MM-dd[T| ]HH:mm:ss(.fff)?" with NO timezone marker
//     → treat as UTC
//   - any other string (ISO-8601 with Z / offset, RFC2822, etc.) → new Date(s)
//
// Use this everywhere a server-supplied timestamp is parsed.

const BARE_LOCAL_LIKE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/

export function parseUtcDate(input) {
  if (input == null || input === '') return null
  // Clone Date inputs — matches `new Date(date)` semantics so callers
  // that mutate the result (e.g. via .setDate(...)) don't accidentally
  // mutate the source.
  if (input instanceof Date) return new Date(input.getTime())
  if (typeof input === 'number') return new Date(input)
  if (typeof input !== 'string') return new Date(input)
  const m = BARE_LOCAL_LIKE.exec(input)
  if (m) {
    const [, y, mo, d, h, mi, se, frac] = m
    const ms = frac ? Number(frac.padEnd(3, '0').slice(0, 3)) : 0
    return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +se, ms))
  }
  // ISO-8601 with TZ marker, RFC2822, etc. — Date can parse these directly.
  return new Date(input)
}

/** Convenience: returns ms-since-epoch (or NaN) for the same input. */
export function parseUtcDateMs(input) {
  const d = parseUtcDate(input)
  return d ? d.getTime() : NaN
}
